import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardHeader, CardContent, CardTitle } from "../components/Card";
import moment from "moment";

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchUser() {
      setLoading(true);
      const ref = doc(db, "users", id);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUser(data);
        setEditFields({
          firstName: data.firstname || "",
          lastName: data.lastname || "",
          ribBic: data.ribBic || "",
          iban: data.iban || "",
          isClient: data.isClient || false,
          isVerified: data.isVerified || false,
        });
      }
      setLoading(false);
    }
    fetchUser();
  }, [id]);

  const saveChanges = async () => {
    setSaving(true);
    const ref = doc(db, "users", id);
    try {
      await updateDoc(ref, {
        firstname: editFields.firstName,
        lastname: editFields.lastName,
        ribBic: editFields.ribBic,
        iban: editFields.iban,
        isClient: editFields.isClient,
        isVerified: editFields.isVerified,
      });
      setUser((prev) => ({
        ...prev,
        firstname: editFields.firstName,
        lastname: editFields.lastName,
        ribBic: editFields.ribBic,
        iban: editFields.iban,
        isClient: editFields.isClient,
        isVerified: editFields.isVerified,
      }));

      // Success notification
      const successDiv = document.createElement("div");
      successDiv.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
      successDiv.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Modifications sauvegardées avec succès !
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 3000);
    } catch (e) {
      alert("Erreur lors de la sauvegarde");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const toggleIsClient = () => {
    setEditFields((f) => ({ ...f, isClient: !f.isClient }));
  };

  const toggleIsVerified = () => {
    if (!(user.pieceRecto && user.pieceVerso)) {
      alert("Impossible de valider: pièces d'identité manquantes.");
      return;
    }
    setEditFields((f) => ({ ...f, isVerified: !f.isVerified }));
  };

  if (!user)
    return (
      <PageTemplate
        loading={loading}
        content={
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                    />
                  </svg>
                </div>
                <p className="text-gray-600 font-medium">Utilisateur introuvable</p>
                <button
                  onClick={() => navigate("/users")}
                  className="mt-4 text-[#801111] hover:text-[#5a0c0c] font-medium"
                >
                  ← Retour à la liste des utilisateurs
                </button>
              </CardContent>
            </Card>
          </div>
        }
      />
    );

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Back Button */}
          <button
            onClick={() => navigate("/users")}
            className="flex items-center gap-2 text-[#801111] hover:text-[#5a0c0c] font-medium transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
            Retour à la liste des utilisateurs
          </button>

          {/* Header Card */}
          <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
            <CardContent>
              <div className="flex items-center gap-4">
                {user.photo ? (
                  <img
                    src={user.photo}
                    alt="Profil"
                    className="w-20 h-20 rounded-full object-cover ring-4 ring-white"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#801111] to-[#5a0c0c] flex items-center justify-center text-white text-2xl font-bold">
                    {user.firstname?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    {user.firstname} {user.lastname}
                  </h1>
                  <p className="text-gray-600">{user.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {user.isVerified && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Vérifié
                      </span>
                    )}
                    {user.isActive && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        ● Actif
                      </span>
                    )}
                    {user.isClient && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Client
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Edit Form Card */}
            <Card>
              <CardHeader>
                <CardTitle>Informations modifiables</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={editFields.firstName}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, firstName: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={editFields.lastName}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, lastName: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent"
                  />
                </div>

                {/* IBAN */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IBAN
                  </label>
                  <input
                    type="text"
                    value={editFields.iban}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, iban: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent font-mono text-sm"
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                  />
                </div>

                {/* BIC */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    BIC/SWIFT
                  </label>
                  <input
                    type="text"
                    value={editFields.ribBic}
                    onChange={(e) =>
                      setEditFields((f) => ({ ...f, ribBic: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#801111] focus:border-transparent font-mono text-sm"
                    placeholder="XXXXXXXX"
                  />
                </div>

                {/* Toggle Client */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">
                    Statut client
                  </span>
                  <button
                    onClick={toggleIsClient}
                    className={`px-4 py-2 rounded-lg text-white font-medium transition ${
                      editFields.isClient
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-gray-400 hover:bg-gray-500"
                    }`}
                    type="button"
                  >
                    {editFields.isClient ? "Oui" : "Non"}
                  </button>
                </div>

                {/* Toggle Verified */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-700 block">
                      Compte vérifié
                    </span>
                    {!(user.pieceRecto && user.pieceVerso) && (
                      <span className="text-xs text-orange-600">
                        ⚠ Documents manquants
                      </span>
                    )}
                  </div>
                  <button
                    onClick={toggleIsVerified}
                    className={`px-4 py-2 rounded-lg text-white font-medium transition ${
                      editFields.isVerified
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-gray-400 hover:bg-gray-500"
                    }`}
                    type="button"
                  >
                    {editFields.isVerified ? "Oui" : "Non"}
                  </button>
                </div>

                {/* Save Button */}
                <button
                  disabled={saving}
                  onClick={saveChanges}
                  className="w-full bg-[#801111] text-white py-2.5 px-4 rounded-lg font-medium hover:bg-[#5a0c0c] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sauvegarde en cours...
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                      </svg>
                      Sauvegarder les modifications
                    </>
                  )}
                </button>
              </CardContent>
            </Card>

            {/* Info & Documents Card */}
            <div className="space-y-6">
              {/* Account Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Informations du compte</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Date de création
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {user.createdAt
                          ? moment(user.createdAt.toDate()).format("LL à HH:mm")
                          : "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Compte actif
                      </p>
                      <p
                        className={`text-xs font-semibold ${
                          user.isActive ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {user.isActive ? "✓ Activé" : "✗ Désactivé"}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const ref = doc(db, "users", id);
                          await updateDoc(ref, { isActive: !user.isActive });
                          setUser((prev) => ({ ...prev, isActive: !prev.isActive }));

                          // Success notification
                          const successDiv = document.createElement("div");
                          successDiv.className =
                            "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
                          successDiv.innerHTML = `
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                            </svg>
                            Compte ${!user.isActive ? "activé" : "désactivé"} !
                          `;
                          document.body.appendChild(successDiv);
                          setTimeout(() => successDiv.remove(), 3000);
                        } catch {
                          alert("Erreur lors de la mise à jour isActive");
                        }
                      }}
                      className={`px-4 py-2 rounded-lg text-white font-medium transition ${
                        user.isActive
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                    >
                      {user.isActive ? "Désactiver" : "Activer"}
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Documents Card */}
              <Card>
  <CardHeader>
    <CardTitle>Documents d'identité</CardTitle>
  </CardHeader>
  <CardContent>
    {user.pieceRecto && user.pieceVerso ? (
      <div className="grid grid-cols-2 gap-4">
        <a
          href={user.pieceRecto}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block border-2 border-gray-200 rounded-lg overflow-hidden hover:border-[#801111] transition-colors"
        >
          <img
            src={user.pieceRecto}
            alt="Pièce d'identité Recto"
            loading="lazy"
            className="w-full h-40 object-contain bg-gray-50"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
          </div>
          <p className="text-center text-xs text-gray-600 py-2 bg-gray-50">
            Recto
          </p>
        </a>
        
        <a
          href={user.pieceVerso}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block border-2 border-gray-200 rounded-lg overflow-hidden hover:border-[#801111] transition-colors"
        >
          <img
            src={user.pieceVerso}
            alt="Pièce d'identité Verso"
            loading="lazy"
            className="w-full h-40 object-contain bg-gray-50"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
          </div>
          <p className="text-center text-xs text-gray-600 py-2 bg-gray-50">
            Verso
          </p>
        </a>
      </div>
    ) : (
      <div className="text-center py-8">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-12 h-12 text-gray-400 mx-auto mb-2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
          />
        </svg>
        <p className="text-gray-500 font-medium">
          Aucun document reçu
        </p>
        <p className="text-xs text-gray-400 mt-1">
          L'utilisateur n'a pas encore fourni ses documents d'identité
        </p>
      </div>
    )}
  </CardContent>
</Card>

            </div>
          </div>
        </div>
      }
    />
  );
}